import { Card, Box, Typography, Button, CircularProgress } from "@mui/material";
import React from "react";
import downloadWhite from "../../assets/icons/download-white.svg";
import downloadGrey from "../../assets/icons/download-grey.svg";
import documentIcon from "../../assets/icons/document.svg";
import { CartConfiguration, LooseItem, PurchaseOrderForm } from "../../types";
import {
  GeneratePurchaseOrderRequest,
  postGeneratePurchaseForm,
} from "../../api/purchaseOrders/purchaseOrders";
import { getStorageValue } from "../../hooks/useLocalStorage";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import TextContainer from "../TextContainer";
import { getUiVersionLabel } from "../../utils/version";

type EventIds = {
  productIds: string[];
  modelIds: string[];
  productQuantities: number[];
  productAmount: number;
};

interface CartDownloadProps {
  configuration: CartConfiguration[];
  looseParts: LooseItem[];
  purchaseOrderDetails: PurchaseOrderForm | null;
  eventIds: EventIds | null;
  isValid: boolean;
}

const CartDownload: React.FC<CartDownloadProps> = ({
  configuration,
  purchaseOrderDetails,
  looseParts,
  eventIds,
  isValid,
}) => {
  const [downloadFailed, setDownloadFailed] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const trackDownloadPurchaseOrderEvent = (downloadId: string) => {
    const hasAnalyticsConsent =
      getStorageValue("cookiesAnalytics", "false") === "true";

    if (!hasAnalyticsConsent) {
      console.info(
        'Analytics event not catalogued: analytics consent is disabled, so this "Download PO" event was not sent.',
      );
      return;
    }

    const gtag = (window as any).gtag;

    if (typeof gtag !== "function") {
      console.info(
        'Analytics event not catalogued: gtag is not available, so this "Download PO" event was not sent.',
      );
      return;
    }

    gtag("event", "download_purchase_order", {
      download_id: downloadId,
      product_ids: eventIds?.productIds || [],
      model_ids: eventIds?.modelIds || [],
      product_quantities: eventIds?.productQuantities || [],
      PO_items_count: eventIds?.productAmount || 0,
    });
  }

  const handleDownloadPurchaseOrder = async () => {
    setIsLoading(true);
    try {
      const formData: PurchaseOrderForm =
        purchaseOrderDetails || ({} as PurchaseOrderForm);
      const uiVersion = await getUiVersionLabel();

      const payload: GeneratePurchaseOrderRequest = {
        configurations: configuration.map(({ version, ...cartConfiguration }) => ({
          ...cartConfiguration,
        })),
        purchaseOrderDetails: {
          marketingData: formData.marketingData,
          administrativeData: {
            ...formData.administrativeData,
            forMilitaryUse:
              formData.administrativeData.forMilitaryUse === "true",
          },
        },
        looseParts: looseParts.map((item) => ({
          id: item.id,
          name: item.name,
          quantity: item.count,
        })),
        uiVersion,
      };

      const response = await postGeneratePurchaseForm(payload);
      if (!response) {
        setDownloadFailed(true);
        return;
      }

      downloadFile(response);

      trackDownloadPurchaseOrderEvent(crypto.randomUUID());
      setDownloadFailed(false);
    } catch (error) {
      console.error("Error downloading purchase order:", error);
      setDownloadFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Throws on failure
  const downloadFile = (data: any) => {
    const element = document.createElement("a");
    const url = URL.createObjectURL(new Blob([data]));

    try {
      const today = dayjs().format("YYYYMMDD-HHmmss");

      element.href = url;
      element.download = `${purchaseOrderDetails?.marketingData.endUserName}-${today}.xlsx`;
      element.click();

      toast("Download started", {
        position: "bottom-right",
      });
    } finally {
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <Card
      sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
      variant="outlined"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src={documentIcon} alt="document Icon" />
        <Typography variant="h5" color="common.black">
          Download your purchase order
        </Typography>
      </Box>
      <Typography variant="bodyMediumWeightRegular" color="grey.600">
        Click the download button to generate and download the purchase order for the current cart content
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: "2px",
          justifyContent: "center",
          mt: "24px",
        }}
      >
        <Button
          variant="containedAbb"
          onClick={handleDownloadPurchaseOrder}
          disabled={!isValid || isLoading}
          sx={{
            width: "248px",
          }}
        >
          {isLoading ? (
            <CircularProgress color="secondary"  size="20px" />
          ) : (
            <>
              <img
                src={isValid && !isLoading ? downloadWhite : downloadGrey}
                alt="Download Icon"
              />{" "}
            </>
          )}
          {isLoading ? "Downloading..." : "Download"}
        </Button>
      </Box>
      {downloadFailed && (
        <TextContainer
          text="PO Form generation failed, please retry in a while. If issue persist, please request support"
          type="warning"
          sx={{ marginTop: "4px" }}
          close={() => setDownloadFailed(false)}
        />
      )}
    </Card>
  );
};

export default CartDownload;
